import React, { useMemo } from 'react';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useStockDashboardStore } from '../../../store/stockDashboardStore';
import { useWidgetSize } from './useWidgetSize';

interface AlertsWidgetProps {
  title: string;
  accentColor: string;
  bgTint: string;
  isDark: boolean;
}

const LOW_STOCK_THRESHOLD = 2;

const AlertsWidget: React.FC<AlertsWidgetProps> = ({ title, accentColor: _ac, bgTint, isDark }) => {
  const { ref, isXS, isSM } = useWidgetSize();
  const { items, stockUnits } = useInventoryStore();
  const { globalScopeWarehouseId } = useStockDashboardStore();

  const alerts = useMemo(() => {
    const units = globalScopeWarehouseId
      ? stockUnits.filter(u => u.warehouseId === globalScopeWarehouseId && u.status !== 'retired')
      : stockUnits.filter(u => u.status !== 'retired');

    const result: { type: 'warning' | 'info'; text: string }[] = [];

    // Low stock per item
    const inStockByItem = new Map<string, number>();
    for (const unit of units) {
      if (unit.status === 'in stock') {
        inStockByItem.set(unit.itemId, (inStockByItem.get(unit.itemId) ?? 0) + 1);
      }
    }

    for (const item of items) {
      const count = inStockByItem.get(item.id) ?? 0;
      if (count <= LOW_STOCK_THRESHOLD) {
        result.push({
          type: 'warning',
          text: count === 0
            ? `Out of stock: ${item.name}`
            : `Low stock: ${item.name} (${count} in stock)`,
        });
      }
    }

    // Checked-out summary
    const checkedOut = units.filter(u => u.status === 'checked out').length;
    if (checkedOut > 0) {
      result.push({ type: 'info', text: `${checkedOut} unit${checkedOut > 1 ? 's' : ''} currently checked out` });
    }

    return result;
  }, [items, stockUnits, globalScopeWarehouseId]);

  const padC  = isXS ? 'p-2'   : 'p-3';
  const headC = isXS ? 'text-xs' : isSM ? 'text-sm' : 'text-base';
  const textC = isXS ? 'text-[10px]' : 'text-xs';
  const pyC   = isXS ? 'py-1.5' : 'py-2';
  const iconS = isXS ? 10 : 12;

  return (
    <div
      ref={ref}
      className={`h-full flex flex-col ${padC} rounded border overflow-hidden`}
      style={{
        backgroundColor: bgTint || (isDark ? '#20242b' : '#ffffff'),
        borderColor: isDark ? '#343a43' : '#e5e7eb',
      }}
    >
      <h3 className={`${headC} font-semibold mb-2 flex-shrink-0 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
        {title}
      </h3>
      {alerts.length === 0 ? (
        <div className={`flex items-center gap-2 px-2 ${pyC} rounded ${textC} border ${
          isDark ? 'bg-green-950/30 text-green-300 border-green-900/50' : 'bg-green-50 text-green-700 border-green-100'
        }`}>
          <CheckCircle size={iconS} className="flex-shrink-0" />
          <span>All stock levels look good.</span>
        </div>
      ) : (
        <div className="space-y-1.5 overflow-auto flex-1">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-2 px-2 ${pyC} rounded ${textC} border ${
                alert.type === 'warning'
                  ? isDark
                    ? 'bg-amber-950/30 text-amber-300 border-amber-900/50'
                    : 'bg-amber-50 text-amber-700 border-amber-100'
                  : isDark
                    ? 'bg-blue-950/30 text-blue-300 border-blue-900/50'
                    : 'bg-blue-50 text-blue-700 border-blue-100'
              }`}
            >
              {alert.type === 'warning'
                ? <AlertTriangle size={iconS} className="flex-shrink-0 mt-0.5" />
                : <Info size={iconS} className="flex-shrink-0 mt-0.5" />
              }
              <span>{alert.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsWidget;
